import { useCallback } from 'react'
import { ActivityPart } from '@/api/models/activityParts'
import { useActivitiesWithAggregates } from '@/hooks/useActivityParts/useActivitiesWithAggregates'
import { FrontActivityPart } from '@/store/slices/aggregateSlice/types/FrontActivityPart.type'
import { FrontActivityWithAggregate } from '@/store/slices/aggregateSlice/types/FrontActivityWithAggregate.type'

const flattenActivityParts = (
  parts: (FrontActivityPart<FrontActivityWithAggregate> | FrontActivityWithAggregate)[],
): (FrontActivityPart<FrontActivityWithAggregate> | FrontActivityWithAggregate)[] => {
  const flattenedParts: (FrontActivityPart<FrontActivityWithAggregate> | FrontActivityWithAggregate)[] = []

  for (const part of parts) {
    flattenedParts.push(part)

    if ('childActivity' in part && part.childActivity.children) {
      flattenedParts.push(...flattenActivityParts(part.childActivity.children))
    }

    if ('children' in part && part.children) {
      flattenedParts.push(...flattenActivityParts(part.children))
    }
  }

  return flattenedParts
}

export const useFindActivityById = <
  T extends Record<string, any> | FrontActivityPart<FrontActivityWithAggregate> | FrontActivityWithAggregate,
>() => {
  const activitiesWithAggregates = useActivitiesWithAggregates()

  return useCallback(
    (id?: string) => {
      if (!id || !activitiesWithAggregates) return

      return flattenActivityParts([activitiesWithAggregates]).find(
        (part) => ('childActivity' in part && part.childActivity.id === id) || part.id === id,
      ) as T | undefined
    },
    [activitiesWithAggregates],
  )
}

export const useFindActivityByIdAndTree = <
  T extends Record<string, any> | FrontActivityPart<FrontActivityWithAggregate> | FrontActivityWithAggregate,
>() => {
  return useCallback((id: string, activitiesWithAggregates: FrontActivityWithAggregate | null) => {
    if (!id || !activitiesWithAggregates) return

    return flattenActivityParts([activitiesWithAggregates]).find(
      (part) => ('childActivity' in part && part.childActivity.id === id) || part.id === id,
    ) as T | undefined
  }, [])
}

export const useFindActivityPartByPathIdAndTree = <
  T extends Record<string, any> | FrontActivityPart<FrontActivityWithAggregate> | FrontActivityWithAggregate,
>() => {
  return useCallback((activityPathId: string, activitiesWithAggregates: FrontActivityWithAggregate | null) => {
    if (!activityPathId || !activitiesWithAggregates) return

    return flattenActivityParts([activitiesWithAggregates]).find(
      (part) => (part as ActivityPart).activityPathId === activityPathId,
    ) as T | undefined
  }, [])
}
